import { UserRole } from "../types/UserRole";
import { StatusItemStatus, normalizeStatusItemStatus } from "./statusItemHelpers";

/**
 * Get available statuses that a user can set based on their role
 * @param userRole - The user's role
 * @returns Array of statuses the user can set
 */
export function getAvailableStatusesForRole(userRole: UserRole): StatusItemStatus[] {
  switch (userRole) {
    case UserRole.User:
      // Users cannot change status (read-only)
      return [];

    case UserRole.Preparer:
      // Preparers can set Open or Processed
      return ["Open", "Processed"];

    case UserRole.Reviewer:
      // Reviewers can set up to Quality Reviewed
      return ["Open", "Processed", "Rework Required", "Quality Reviewed"];

    case UserRole.Partner:
      // Partners can set all statuses
      return ["Open", "Processed", "Rework Required", "Quality Reviewed", "Partner Reviewed"];

    default:
      return [];
  }
}

/**
 * Check if a user can change status to a specific value
 * @param userRole - The user's role
 * @param targetStatus - The status to change to
 * @returns true if the user can set this status
 */
export function canUserSetStatus(userRole: UserRole, targetStatus: string): boolean {
  const availableStatuses = getAvailableStatusesForRole(userRole);
  const normalizedTarget = normalizeStatusItemStatus(targetStatus);
  return availableStatuses.some((status) => normalizeStatusItemStatus(status) === normalizedTarget);
}

/**
 * Check if a user can delete status items
 * @param userRole - The user's role
 * @returns true if the user can delete status items
 */
export function canUserDeleteStatusItem(userRole: UserRole): boolean {
  // Only Partners can delete status items
  return userRole === UserRole.Partner;
}
